/**
 * Sync add-to-cart form with active custom variant label before submit.
 * Runs in capture phase so it executes before ProductForm's submit handler.
 */
function syncFormWithActiveCustomVariant(form) {
    const productContainer = form.closest('.main-single-product__product, .single-product__product, .custom-products__product') ||
        form.closest('.main-single-product');
    if (!productContainer) return;

    const activeLabel = productContainer.querySelector('.custom-variant-label.is-active') ||
        productContainer.querySelector('.product-form__input[data-two-option-mode] input[type="radio"]:checked + .custom-variant-label');
    if (!activeLabel) return;

    const variantScript = activeLabel.querySelector('script[type="application/ld+json"]');
    const productId = activeLabel.getAttribute('data-product-id');
    const sellingPlanId = activeLabel.getAttribute('data-selling-plan-id');
    if (!variantScript || !productId) return;

    try {
        const variant = JSON.parse(variantScript.innerText);
        const idInput = form.querySelector('[name="id"], .product-variant-id');
        const productIdInput = form.querySelector('input[name="product-id"]');
        const sellingPlanInput = form.querySelector('[name="selling_plan"], #recharge_selling_plan_input');
        if (idInput) {
            idInput.value = variant.id;
            idInput.removeAttribute('disabled');
        }
        if (productIdInput) productIdInput.value = productId;
        if (sellingPlanInput) {
            sellingPlanInput.setAttribute('name', 'selling_plan');
            sellingPlanInput.value = sellingPlanId || '';
            sellingPlanInput.removeAttribute('disabled');
        }
    } catch (err) {
        console.warn('single-product: Could not sync form with custom variant', err);
    }
}

function getTwoOptionFieldset(productContainer) {
    return productContainer?.querySelector('[data-two-option-mode][data-variant-required]');
}

function hasRequiredTwoOptionSelection(productContainer) {
    const fieldset = getTwoOptionFieldset(productContainer);
    if (!fieldset) return true;
    return !!fieldset.querySelector('input[type="radio"]:checked');
}

function setTwoOptionRequiredMessage(productContainer, show) {
    const fieldset = getTwoOptionFieldset(productContainer);
    if (!fieldset) return;
    const msg = fieldset.querySelector('.product-form__variant-required-msg');
    if (msg) msg.hidden = !show;
    fieldset.classList.toggle('is-missing-selection', !!show);
}

function setAddToCartEnabled(product, enabled) {
    const buttons = product.querySelectorAll('.product-form__submit, [add-to-cart-button]');
    buttons.forEach((btn) => {
        if (enabled) {
            btn.removeAttribute('disabled');
            btn.removeAttribute('aria-disabled');
            btn.classList.remove('is-disabled');
        } else {
            btn.setAttribute('disabled', 'disabled');
            btn.setAttribute('aria-disabled', 'true');
            btn.classList.add('is-disabled');
        }
    });
}

function syncFormBeforeAddToCart(form) {
    if (!form || form.getAttribute?.('data-type') !== 'add-to-cart-form') return;
    const productContainer = form.closest('.main-single-product__product, .single-product__product, .custom-products__product') ||
        form.closest('.main-single-product');
    if (productContainer?.querySelector('.custom-variant-label')) {
        syncFormWithActiveCustomVariant(form);
    }
}

/**
 * Activate a custom product option (DREAM™ / melatonin-free).
 * Runs on user selection so ReCharge subscription updates for the chosen product.
 */
function activateCustomVariantLabel(product, swatch, options = {}) {
    const { reloadWidget = true, updateButton = true } = options;
    if (!product || !swatch || swatch.classList.contains('is-disabled')) return;

    const productIdInput = product.querySelector('input[name="product-id"]');
    const variantIdInput = product.querySelector('.product-variant-id, [name="id"]');
    const sellingPlanInput = product.querySelector('[name="selling_plan"], #recharge_selling_plan_input');
    const addToCartBtn = product.querySelector('.product-form__submit span');
    const btn = product.querySelector('.product-form__submit');
    const swatches = product.querySelectorAll('.custom-variant-label');
    const swatchInputs = product.querySelectorAll('.single-product__variants input[type="radio"], .main-single-product__variants input[type="radio"]');

    const currentProductId = swatch.getAttribute('data-product-id');
    const variantScript = swatch.querySelector('script[type="application/ld+json"]');
    let currentVariant = null;
    if (variantScript) {
        try {
            currentVariant = JSON.parse(variantScript.innerText);
        } catch (e) {
            console.warn('single-product: Could not parse variant JSON', e);
        }
    }

    swatches.forEach((label) => label.classList.remove('is-active'));
    swatch.classList.add('is-active');

    const associatedInput = swatch.htmlFor
        ? document.getElementById(swatch.htmlFor)
        : swatch.previousElementSibling?.type === 'radio'
            ? swatch.previousElementSibling
            : null;
    if (associatedInput && associatedInput.type === 'radio' && !associatedInput.checked) {
        associatedInput.checked = true;
    }

    swatchInputs.forEach((input) => {
        if (associatedInput && input === associatedInput) return;
        if (input.name && associatedInput && input.name === associatedInput.name) return;
        if (input.closest('[data-two-option-mode]')) return;
        input.checked = false;
    });

    setTwoOptionRequiredMessage(product, false);
    setAddToCartEnabled(product, true);

    if (updateButton && btn) {
        if (btn.hasAttribute('data-btn-custom-name') && btn.querySelector('span')) {
            const parent = btn.closest('.main-single-product__product');
            const subInput = parent?.querySelector('input[data-radio-subsave]');
            let price = btn.getAttribute('data-onetime-price');
            let originalPrice = '';

            if (subInput && subInput.checked) {
                price = btn.getAttribute('data-subscribe-price');
                if (btn.hasAttribute('data-original-price')) {
                    originalPrice = '<i>' + btn.getAttribute('data-original-price') + '</i>';
                }
            }

            btn.querySelector('span').innerHTML = btn.getAttribute('data-btn-custom-name') + originalPrice + ' ' + price;
        } else if (addToCartBtn) {
            addToCartBtn.innerText = 'BUY NOW';
        }
    }

    if (productIdInput && currentProductId) productIdInput.value = currentProductId;
    if (variantIdInput && currentVariant?.id) {
        variantIdInput.value = currentVariant.id;
        variantIdInput.removeAttribute('disabled');
    }
    if (sellingPlanInput) {
        const labelSellingPlanId = swatch.getAttribute('data-selling-plan-id');
        const variantSellingPlanId =
            currentVariant?.selling_plan_allocations?.[0]?.selling_plan_id ||
            currentVariant?.selling_plan_allocations?.[0]?.selling_plan?.id;
        sellingPlanInput.setAttribute('name', 'selling_plan');
        sellingPlanInput.value = labelSellingPlanId || variantSellingPlanId || sellingPlanInput.value || '';
        sellingPlanInput.removeAttribute('disabled');
        if (sellingPlanInput.value) {
            sellingPlanInput.setAttribute('data-selling-plan-backup', sellingPlanInput.value);
        }
    }
    if (product && currentProductId) product.setAttribute('data-product-id', String(currentProductId));

    const variantsData = product?.querySelector('.custom-variant-product-variants[data-product-id="' + currentProductId + '"]');
    const variants = variantsData
        ? (() => {
              try {
                  return JSON.parse(variantsData.textContent);
              } catch (e) {
                  return null;
              }
          })()
        : null;
    if (variants && variants.length >= 2 && typeof window.updateRcVariantSelectorForProduct === 'function') {
        window.updateRcVariantSelectorForProduct(currentProductId, variants);
    }

    const previousProductId = product.getAttribute('data-last-custom-product-id');
    const productChanged = !previousProductId || previousProductId !== String(currentProductId);
    product.setAttribute('data-last-custom-product-id', String(currentProductId));

    if (!reloadWidget || !productChanged) return;

    const currentContainer = product.querySelector('.recharge-widget-container');
    if (currentContainer && typeof window.ReChargeWidget !== 'undefined' && currentProductId) {
        const widgetHeight = currentContainer.offsetHeight || 80;
        currentContainer.style.height = widgetHeight + 'px';
        currentContainer.innerHTML = '';
        if (typeof window.ReChargeWidget.destroyWidgets === 'function') window.ReChargeWidget.destroyWidgets();
        if (window.ReChargeWidget?.api?.fetchProduct) {
            try {
                window.ReChargeWidget.api.fetchProduct(currentProductId);
            } catch (e) {}
        }
        setTimeout(() => {
            const config = {
                productId: currentProductId,
                defaultVariantId: currentVariant?.id ? String(currentVariant.id) : undefined,
                injectionMethod: 'prepend',
                injectionParent: currentContainer,
            };
            window.ReChargeWidget.createWidget(config);
        }, 100);
        setTimeout(() => {
            currentContainer.style.height = '';
        }, 800);
    }
    const rsw = product.querySelector('recharge-subscription-widget') || document.querySelector('recharge-subscription-widget');
    if (rsw && currentProductId) {
        const newRsw = document.createElement('recharge-subscription-widget');
        newRsw.setAttribute('product-id', String(currentProductId));
        if (currentVariant?.id) newRsw.setAttribute('default-variant-id', String(currentVariant.id));
        rsw.replaceWith(newRsw);
    }
}

function blockAddWithoutTwoOptionSelection(e, form, productContainer) {
    if (!productContainer || hasRequiredTwoOptionSelection(productContainer)) return false;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    setTwoOptionRequiredMessage(productContainer, true);
    const fieldset = getTwoOptionFieldset(productContainer);
    fieldset?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstRadio = fieldset?.querySelector('input[type="radio"]:not(:disabled)');
    firstRadio?.focus();
    return true;
}

document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || form.getAttribute?.('data-type') !== 'add-to-cart-form') return;
    const productContainer = form.closest('.main-single-product__product, .single-product__product, .custom-products__product') ||
        form.closest('.main-single-product');
    if (blockAddWithoutTwoOptionSelection(e, form, productContainer)) return;
    syncFormBeforeAddToCart(form);
}, true);

document.addEventListener('click', function (e) {
    const btn = e.target.closest?.('[add-to-cart-button], button[form][type="button"], .product-form__submit');
    if (!btn) return;
    const formId = btn.getAttribute?.('form');
    const form = formId ? document.getElementById(formId) : btn.closest?.('form');
    const productContainer = (form || btn).closest?.('.main-single-product__product, .single-product__product, .custom-products__product') ||
        (form || btn).closest?.('.main-single-product');
    if (blockAddWithoutTwoOptionSelection(e, form, productContainer)) return;
    syncFormBeforeAddToCart(form);
}, true);

document.addEventListener('DOMContentLoaded', () => {
    let products = document.querySelectorAll('.single-product__product, .custom-products__product, .main-single-product__product');

    if (products.length) {
        products.forEach((product) => {
            const swatches = product.querySelectorAll('.single-product__variants label, .main-single-product__variants label, .custom-variant-label'),
                productIdInput = product.querySelector('input[name="product-id"]'),
                variantIdInput = product.querySelector('.product-variant-id'),
                sellingPlanInput = product.querySelector('[name="selling_plan"]'),
                productID = product.getAttribute('data-product-id'),
                variantID = product.getAttribute('data-variant'),
                sellingPlanID = product.querySelector('[name="selling_plan"]')?.value,
                btn = product.querySelector('.product-form__submit');

            const requiredFieldset = getTwoOptionFieldset(product);
            if (requiredFieldset) {
                // No default selection — keep both unchecked and disable add to cart
                requiredFieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
                    radio.checked = false;
                });
                requiredFieldset.querySelectorAll('.custom-variant-label').forEach((label) => {
                    label.classList.remove('is-active');
                });
                setAddToCartEnabled(product, false);
                setTwoOptionRequiredMessage(product, false);
            }

            if (swatches.length) {
                swatches.forEach((swatch) => {
                    swatch.addEventListener('click', (e) => {
                        if (swatch.classList.contains('is-disabled')) return;

                        const isCustomVariantLabel = swatch.classList.contains('custom-variant-label');
                        const previousElement = swatch.previousElementSibling;
                        const isStandardVariantRadio =
                            previousElement &&
                            previousElement.tagName === 'INPUT' &&
                            previousElement.type === 'radio' &&
                            !isCustomVariantLabel;

                        if (isStandardVariantRadio) {
                            swatches.forEach((label) => {
                                label.classList.remove('is-active');
                            });
                            if (productIdInput && productID) productIdInput.value = productID;
                            if (variantIdInput && variantID) variantIdInput.value = variantID;
                            if (product.classList.contains('single-product__product') || product.classList.contains('main-single-product__product')) {
                                if (sellingPlanInput && sellingPlanID) sellingPlanInput.value = sellingPlanID;
                            } else {
                                const currentContainer = product.querySelector('.recharge-widget-container');

                                if (currentContainer) {
                                    const widgetHeight = currentContainer.offsetHeight;
                                    currentContainer.style.height = `${widgetHeight}px`;

                                    currentContainer.innerHTML = '';

                                    setTimeout(() => {
                                        const config = {
                                            productId: productID,
                                            injectionMethod: 'prepend',
                                            injectionParent: currentContainer,
                                        };
                                        window.ReChargeWidget.createWidget(config);
                                    }, 0);

                                    setTimeout(() => {
                                        currentContainer.style.height = '';
                                    }, 500);
                                }
                            }
                        } else if (isCustomVariantLabel) {
                            const associatedRadio =
                                (previousElement && previousElement.type === 'radio' && previousElement) ||
                                (swatch.htmlFor && document.getElementById(swatch.htmlFor)?.type === 'radio'
                                    ? document.getElementById(swatch.htmlFor)
                                    : null);
                            // Change handler in recharge-variant-selector.js reloads the widget
                            activateCustomVariantLabel(product, swatch, {
                                reloadWidget: !associatedRadio,
                                updateButton: true,
                            });
                        }

                        if (btn && hasRequiredTwoOptionSelection(product)) {
                            btn.setAttribute('disabled', 'disabled');
                            setTimeout(() => {
                                setAddToCartEnabled(product, true);
                            }, 500);
                        }
                    });
                });
            }
        });
    }
});
